import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { GraduationCap } from '@phosphor-icons/react';

import Button from '../Button';
import { buildTutorials } from './tutorialSteps';
import { isTutorialComplete, readProgress, readStoredUserEmail } from './tutorialProgress';
import { openTutorialMenu as openTutorialMenuAction } from '../../actions/tutorial';
import { tutorialAvailabilityContext } from './tutorialAvailability';
import { captureAnalytics } from '../../actions/preload';
import './tutorial.scss';

/**
 * "Learn by doing" launcher, pinned under the Guide's section links.
 *
 * The Guide already has an Interactive Tutorials page (GuideTutorials), but it is
 * one item among seventeen in the sidebar, so a user reading any other section has
 * no idea the guided tutorials exist. This card rides along with the Guide's
 * right-hand rail — i.e. it is on screen for EVERY guide section, not just one —
 * and opens the same "Learn Medsender" checklist the account menu opens.
 *
 * It opens the checklist rather than starting a specific tutorial: which one to run
 * is the user's choice, and the checklist is where completion state lives.
 * `openTutorialMenu` is all that is needed because the app-wide LearnMedsender
 * container renders the panel and owns the launch sequence from there.
 */
export const GuideTutorialLaunchBase = ({
  userEmail,
  hasEmrIntegration,
  impersonating,
  openTutorialMenu,
}) => {
  // Nothing tutorial-related is offered while impersonating: LearnMedsender does
  // not render, so this card's button would open a panel that never appears.
  if (impersonating) return null;

  const progress = readProgress(userEmail);
  // `hasRecords: true` mirrors GuideTutorials: availability is decided against the
  // live DOM when a tutorial actually starts, never pre-locked from record counts.
  const tutorials = buildTutorials({ hasEmrIntegration, ...tutorialAvailabilityContext() });
  const completedCount = tutorials.filter((tutorial) => (
    isTutorialComplete(progress, tutorial.id)
  )).length;
  const allComplete = tutorials.length > 0 && completedCount === tutorials.length;

  const launch = () => {
    captureAnalytics('tutorial_menu_opened', { source: 'guide_rail' });
    openTutorialMenu();
  };

  return (
    <aside className="guide-tutorial-launch" data-testid="guide-tutorial-launch">
      <h3 className="guide-tutorial-launch__title">
        <GraduationCap size={16} weight="bold" aria-hidden="true" />
        Learn by doing
      </h3>
      <p className="guide-tutorial-launch__body">
        Short, hands-on walkthroughs that point at the real buttons and let you
        click through them yourself. Stop any of them at any time.
      </p>
      <p className="guide-tutorial-launch__progress">
        {`${completedCount} of ${tutorials.length} complete`}
      </p>
      {/* `tertiary` (purple on white), not the solid `primary` fill: this card
          shares a narrow rail with navigation, so a full-strength CTA competed
          with the page it is meant to sit beside. */}
      <Button
        type="tertiary"
        size="xsmall"
        fullWidth
        label={allComplete ? 'Replay a tutorial' : 'Start a tutorial'}
        onClick={launch}
      />
    </aside>
  );
};

GuideTutorialLaunchBase.defaultProps = {
  userEmail: '',
  hasEmrIntegration: false,
  impersonating: false,
  // Matches the other openTutorialMenu call site (components/Avatar): the test
  // harness stubs react-redux's connect as a pass-through, so a required prop here
  // would warn on every render of the Guide in tests without catching anything.
  openTutorialMenu: () => {},
};

GuideTutorialLaunchBase.propTypes = {
  userEmail: PropTypes.string,
  hasEmrIntegration: PropTypes.bool,
  impersonating: PropTypes.bool,
  openTutorialMenu: PropTypes.func,
};

export default connect((state) => ({
  // Same resolution order as LearnMedsender: `m8_uid` first, because it is the one
  // value logout clears and every login path rewrites, so it cannot name the
  // previously signed-in account the way the reducer could.
  userEmail: readStoredUserEmail() || state.userProfile.email || state.auth.data?.uid || '',
  hasEmrIntegration: !!state.auth.hasEmrIntegrationCredential || !!state.auth.data?.autoUploadEmr,
  impersonating: !!state.auth.impersonating,
}), {
  openTutorialMenu: openTutorialMenuAction,
})(GuideTutorialLaunchBase);
